import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import TagInput from "@/components/TagInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

const VOICES = ["Professional", "Conversational", "Energetic", "Premium", "Straightforward"];
const CTA_OPTIONS = ["Contact Us", "View Inventory", "Schedule a Visit", "Request Information", "Ask About This Boat"];
const GUIDANCE_MAX = 1000;

const EMPTY = {
  default_brand_voice: "", preferred_ctas: [], phrases_to_avoid: [],
  differentiators: "", additional_guidance: "",
};

export default function MarketingPreferences() {
  const [prefs, setPrefs] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/dealership")
      .then(({ data }) => setPrefs({ ...EMPTY, ...(data.marketing_preferences || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleCta = (cta) => setPrefs((p) => ({
    ...p,
    preferred_ctas: p.preferred_ctas.includes(cta)
      ? p.preferred_ctas.filter((c) => c !== cta)
      : [...p.preferred_ctas, cta],
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dealership", { marketing_preferences: prefs });
      toast.success("Marketing preferences saved");
    } catch {
      toast.error("Could not save marketing preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-6 space-y-5" data-testid="marketing-preferences">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-navy/5 flex items-center justify-center"><MessageSquareText className="w-5 h-5 text-navy" /></div>
        <div>
          <h2 className="font-serif text-lg font-bold text-navy">Marketing Preferences</h2>
          <p className="text-sm text-muted-foreground">Help RecJet tailor campaign writing to your dealership. These preferences affect brand voice and wording, but never override RecJet accuracy, pricing, image, compliance, or RecJet Mind strategy rules.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading preferences...</div>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label>Default Brand Voice</Label>
            <div className="flex flex-wrap gap-2" data-testid="brand-voice-options">
              {VOICES.map((v) => (
                <button key={v} type="button" onClick={() => setPrefs((p) => ({ ...p, default_brand_voice: p.default_brand_voice === v ? "" : v }))}
                  data-testid={`brand-voice-${v.toLowerCase()}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${prefs.default_brand_voice === v ? "bg-navy text-white border-navy" : "bg-white text-navy border-border hover:bg-secondary"}`}>
                  {v}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">A default writing preference only. A specific campaign tone may override this, and RecJet Mind strategy stays authoritative for RecJet Mind campaigns.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Preferred Calls to Action</Label>
            <div className="flex flex-wrap gap-2" data-testid="cta-options">
              {CTA_OPTIONS.map((c) => (
                <button key={c} type="button" onClick={() => toggleCta(c)}
                  data-testid={`cta-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${prefs.preferred_ctas.includes(c) ? "bg-gold text-navy border-gold" : "bg-white text-navy border-border hover:bg-secondary"}`}>
                  {c}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Used to guide wording only when appropriate to the campaign.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Phrases to Avoid</Label>
            <TagInput tags={prefs.phrases_to_avoid} onChange={(t) => setPrefs((p) => ({ ...p, phrases_to_avoid: t }))} placeholder="Type a phrase and press Enter" testid="phrases-to-avoid" />
            <p className="text-xs text-muted-foreground">Phrases you do not want used. This never removes required compliance language such as SMS opt-out text.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Dealership Differentiators</Label>
            <Textarea value={prefs.differentiators} onChange={(e) => setPrefs((p) => ({ ...p, differentiators: e.target.value }))} rows={3}
              placeholder="e.g. Family-owned dealership. Factory-trained service department. Serving the area since 1998." data-testid="differentiators-input" />
            <p className="text-xs text-muted-foreground">Only factual statements you enter are used. RecJet never infers or fabricates these.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Additional Marketing Guidance</Label>
            <Textarea value={prefs.additional_guidance}
              onChange={(e) => setPrefs((p) => ({ ...p, additional_guidance: e.target.value.slice(0, GUIDANCE_MAX) }))}
              rows={3} placeholder="e.g. Keep copy direct and avoid overly aggressive sales language." data-testid="additional-guidance-input" />
            <p className="text-xs text-muted-foreground">{prefs.additional_guidance.length}/{GUIDANCE_MAX}. Style guidance only. It never overrides RecJet accuracy, pricing, image, compliance, or strategy rules.</p>
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light text-white" data-testid="save-marketing-preferences-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Save Preferences
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
